import { LoadingButton } from '@mui/lab';
import { Alert, Button, Collapse, Divider, Grid, TextField } from '@mui/material';
import { FormikValues } from 'formik';
import { isString } from 'lodash';
import { ComponentType } from 'react';
import { useTranslation } from 'react-i18next';
import { useLanguageTabs } from 'src/hooks/useLanguageTabs';
import { useEdit } from '../../../hooks';
import {
  clearRegion,
  clearRegionRequest,
  createRegion,
  editRegion,
  forceGetRegionWarehouse,
  getRegion,
} from '../../../thunks/region-thunks';
import {
  FIELD_CODE,
  FIELD_TITLE,
  FIELD_TITLE_OZ,
  FIELD_TITLE_RU,
  FIELD_TITLE_UZ,
  RegionsEditValidationForm,
} from './regions-edit-schema';

export interface RegionsEditFormProps {
  id?: string | number;
  onCancel?: () => void;
  onSubmit: (values: FormikValues) => void;
}

export const RegionsEditForm: ComponentType<RegionsEditFormProps> = ({
  id,
  onCancel,
  onSubmit,
}) => {
  const { t } = useTranslation();
  const { tabLang, renderTabsButtons } = useLanguageTabs();
  const { isLoading, isFetching, error, formik, handleFieldChange, hasError, getErrorMessage } =
    useEdit({
      id,
      entity: 'region',
      initialValues: {
        [FIELD_TITLE]: {
          ru: '',
          uz: '',
          oz: '',
        },
        [FIELD_CODE]: '',
      },
      validationSchema: RegionsEditValidationForm,
      onGetWareHouse: forceGetRegionWarehouse,
      onGetOne: getRegion,
      onClearOne: clearRegion,
      onClear: clearRegionRequest,
      onCreate: createRegion,
      onEdit: editRegion,
      onSubmit: onSubmit,
    });

  return (
    <form onSubmit={formik.handleSubmit}>
      <Grid container mb={3} direction="column">
        <Grid item mb={2}>
          {renderTabsButtons()}
        </Grid>
        {tabLang === 'ru' && (
          <Grid item>
            <TextField
              {...formik.getFieldProps(`${FIELD_TITLE_RU}`)}
              onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_RU}`, e)}
              error={hasError(`${FIELD_TITLE_RU}`)}
              helperText={getErrorMessage(`${FIELD_TITLE_RU}`)}
              label={`${t('regions.title.label', 'Название региона')} (${t('russian', 'русский')})`}
              placeholder={t('regions.title.placeholder', 'Введите название региона')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        {tabLang === 'uz' && (
          <Grid item>
            <TextField
              {...formik.getFieldProps(`${FIELD_TITLE_UZ}`)}
              onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_UZ}`, e)}
              error={hasError(`${FIELD_TITLE_UZ}`)}
              helperText={getErrorMessage(`${FIELD_TITLE_UZ}`)}
              label={`${t('regions.title.label', 'Название региона')} (${t('uzbek', 'узбекский')})`}
              placeholder={t('regions.title.placeholder', 'Введите название региона')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        {tabLang === 'oz' && (
          <Grid item>
            <TextField
              {...formik.getFieldProps(`${FIELD_TITLE_OZ}`)}
              onChange={(e: any) => handleFieldChange(`${FIELD_TITLE_OZ}`, e)}
              error={hasError(`${FIELD_TITLE_OZ}`)}
              helperText={getErrorMessage(`${FIELD_TITLE_OZ}`)}
              label={`${t('regions.title.label', 'Название региона')} (${t('ozbek')})`}
              placeholder={t('regions.title.placeholder', 'Введите название региона')}
              disabled={isLoading || isFetching}
              fullWidth
            />
          </Grid>
        )}
        <Grid item>
          <TextField
            {...formik.getFieldProps(FIELD_CODE)}
            onChange={(e: any) => handleFieldChange(FIELD_CODE, e)}
            error={hasError(FIELD_CODE)}
            helperText={getErrorMessage(FIELD_CODE)}
            label={t('regions.code.label', 'Код региона')}
            placeholder={t('regions.code.placeholder', 'Введите код региона')}
            disabled={isLoading || isFetching}
            fullWidth
          />
        </Grid>
      </Grid>
      {isString(error) && (
        <Collapse in={!!error}>
          <Alert severity="error">{error}</Alert>
        </Collapse>
      )}
      <Divider sx={{ mt: 3, mb: 4 }} />
      <Grid container columnSpacing={2} justifyContent="space-between">
        <Grid item>
          {onCancel && (
            <Button onClick={onCancel} variant="outlined" size="large" disabled={isLoading}>
              {t('cancel', 'Отмена')}
            </Button>
          )}
        </Grid>
        <Grid item>
          <LoadingButton
            loading={isLoading || isFetching}
            type="submit"
            variant="contained"
            color="secondary"
            size="large"
          >
            {t('action.save', 'Сохранить')}
          </LoadingButton>
        </Grid>
      </Grid>
    </form>
  );
};
